import React from 'react'
import {redirect, useFetcher, useRouteLoaderData} from '@remix-run/react'
import type {ActionFunctionArgs, LoaderFunction} from '@remix-run/node'
import clsx from 'clsx'
import {getTranslation} from '~/localization/translation'

import {sharingPolicyCookie} from '~/utils/cookies.server'
import type {RootLoaderResponse} from '~/types'
import {SuccessCheckIcon} from '~/icons/sharing-policy/success-icon'
import Spinner from '~/components/commons/spinner'

export const loader: LoaderFunction = async ({request}) => {
  const url = new URL(request.url)
  const success = url.searchParams.get('success')

  if (success === 'true') {
    return redirect('/sharing-policy', {status: 302})
  }

  return null
}

export async function action({request}: ActionFunctionArgs) {
  const form = await request.formData()
  const requested = form.get('sharing_policy_request') === 'true'

  const cookieValue = {sharingPolicyRequest: requested}

  return redirect('/sharing-policy?success=true', {
    status: 302,
    headers: {
      'Set-Cookie': await sharingPolicyCookie.serialize(cookieValue),
    },
  })
}

export default function SharingPolicy() {
  const {language, sharingPolicy} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const fetcher = useFetcher()
  const {requested} = sharingPolicy ?? {}
  const isLoading = fetcher.state === 'submitting' || fetcher.state === 'loading'

  return (
    <main className={clsx('min-h-screen px-10 pb-10 text-sm tablet:px-5')}>
      <div className="content-container mx-auto mt-16 flex flex-col items-center justify-start gap-10 tablet:mt-6 tablet:gap-4">
        <h1 className="mx-auto flex w-fit text-center text-5xl font-semibold capitalize leading-9 tablet:text-[28px]">
          {translations.SharingPolicyPage.title}
        </h1>
        <div className="w-full max-w-[900px] border-b-4 border-[#FFC711] tablet:max-w-36" />
        <p className="mt-5 max-w-[1000px] text-center text-[18px] font-light leading-6 text-[#363636]">
          {translations.SharingPolicyPage.content}
        </p>
        <div className="mb-10 mt-3 flex flex-col items-center justify-center gap-5">
          {requested ? (
            <>
              <div className="size-16 tablet:mt-5">
                <SuccessCheckIcon className="size-16 animate-[fade-in_0.1s_ease-in_forwards]" />
              </div>
              <p className="text-center text-[18px] font-light leading-6 text-[#363636]">
                {translations.SharingPolicyPage.feedbackMessage}
              </p>
            </>
          ) : (
            <button
              type="button"
              aria-label="submit request"
              disabled={isLoading}
              className="flex h-[42px] flex-row items-center justify-center gap-2 rounded-[52px] bg-[#363636] px-5 font-light text-white disabled:text-gray-300 disabled:opacity-80"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  if (typeof window.fbq !== 'undefined') {
                    window.fbq('consent', 'revoke', {})
                  }
                  if (window.gtag) {
                    window.gtag('consent', 'update', {
                      analytics_storage: 'denied',
                      ad_storage: 'denied',
                      ad_user_data: 'denied',
                      ad_personalization: 'denied',
                    })
                    window.gtag = function () {}
                    window.dataLayer = []
                  }
                  window.dispatchEvent(new CustomEvent('sharing-policy-submitted'))
                }
                const formData = new FormData()
                formData.set('sharing_policy_request', 'true')
                fetcher.submit(formData, {method: 'post'})
              }}
            >
              {translations.SharingPolicyPage.submit(isLoading)}
              {isLoading && <Spinner />}
            </button>
          )}
        </div>
      </div>
    </main>
  )
}
